<template>
  <div class="admin-statistics">
    <!-- Header -->
    <div class="page-header">
      <h1>📊 İstatistikler</h1>
      <p>Sistem geneli istatistikler ve performans metrikleri</p>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="loading-state">
      <div class="loading-spinner"></div>
      <p>İstatistikler yükleniyor...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-state">
      <div class="error-icon">⚠️</div>
      <h3>Hata Oluştu</h3>
      <p>{{ error }}</p>
      <button @click="fetchStatistics" class="btn-retry">🔄 Tekrar Dene</button>
    </div>

    <!-- Statistics Content -->
    <div v-else-if="statistics" class="statistics-content">
      <!-- Overview Cards -->
      <div class="overview-cards">
        <div class="stat-card total-students">
          <div class="stat-icon">👥</div>
          <div class="stat-content">
            <span class="stat-number">{{ statistics.overview.total_students }}</span>
            <span class="stat-label">Toplam Öğrenci</span>
          </div>
        </div>
        
        <div class="stat-card total-teachers">
          <div class="stat-icon">👨‍🏫👩‍🏫</div>
          <div class="stat-content">
            <span class="stat-number">{{ statistics.overview.total_teachers }}</span>
            <span class="stat-label">Toplam Öğretmen</span>
          </div>
        </div>
        
        <div class="stat-card total-appointments">
          <div class="stat-icon">📅</div>
          <div class="stat-content">
            <span class="stat-number">{{ statistics.overview.total_appointments }}</span>
            <span class="stat-label">Toplam Randevu</span>
          </div>
        </div>
        
        <div class="stat-card current-week">
          <div class="stat-icon">📊</div>
          <div class="stat-content">
            <span class="stat-number">{{ statistics.overview.current_week_appointments }}</span>
            <span class="stat-label">Bu Hafta</span>
          </div>
        </div>
      </div>

      <!-- Weekly Statistics Chart -->
      <div class="chart-section">
        <h2>📈 Haftalık Randevu Sayıları</h2>
        <div class="weekly-chart">
          <div v-for="week in weeklyStats" :key="week.week_start" class="week-bar">
            <div class="bar-container">
              <div 
                class="bar" 
                :style="{ height: getBarHeight(week.appointment_count) }"
                :title="`${week.appointment_count} randevu`"
              ></div>
            </div>
            <div class="week-label">
              <span class="week-number">{{ week.week_number }}. Hafta</span>
              <span class="week-date">{{ formatWeekDate(week.week_start) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Top Performers Section -->
      <div class="top-performers">
        <div class="top-students">
          <h2>🏆 En Aktif Öğrenciler</h2>
          <div class="performer-list">
            <div v-for="(student, index) in statistics.top_students" :key="student.student_id" class="performer-item">
              <div class="rank">{{ index + 1 }}</div>
              <div class="performer-info">
                <span class="name">{{ student.student_name }}</span>
                <span class="count">{{ student.appointment_count }} randevu</span>
              </div>
            </div>
          </div>
        </div>
        
        <div class="top-teachers">
          <h2>🏆 En Aktif Öğretmenler</h2>
          <div class="performer-list">
            <div v-for="(teacher, index) in statistics.top_teachers" :key="teacher.teacher_id" class="performer-item">
              <div class="rank">{{ index + 1 }}</div>
              <div class="performer-info">
                <span class="name">{{ teacher.teacher_name }}</span>
                <span class="count">{{ teacher.appointment_count }} randevu</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Weekly Statistics Table -->
      <div class="table-section">
        <h2>📋 Haftalık Detaylar</h2>
        <div class="stats-table">
          <div class="table-header">
            <div class="header-cell">Hafta</div>
            <div class="header-cell">Tarih</div>
            <div class="header-cell">Randevu Sayısı</div>
          </div>
          
          <div class="table-row" v-for="week in weeklyStats" :key="week.week_start">
            <div class="table-cell">{{ week.week_number }}. Hafta</div>
            <div class="table-cell">{{ formatWeekDate(week.week_start) }}</div>
            <div class="table-cell">
              <span class="appointment-count">{{ week.appointment_count }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- No Data State -->
    <div v-else class="no-data-state">
      <div class="no-data-icon">📊</div>
      <h3>Henüz İstatistik Yok</h3>
      <p>Sistemde henüz randevu bulunmuyor.</p>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import axios from 'axios'

export default {
  name: 'AdminStatisticsView',
  setup() {
    const store = useStore()
    const router = useRouter()
    
    const isLoading = ref(false)
    const error = ref('')
    const statistics = ref(null)
    
    // Computed properties
    const currentUser = computed(() => store.getters.getCurrentUser)
    const weeklyStats = computed(() => {
      if (!statistics.value?.weekly_statistics) return []
      return statistics.value.weekly_statistics.sort((a, b) => a.week_number - b.week_number)
    })
    
    // Methods
    const fetchStatistics = async () => {
      isLoading.value = true
      error.value = ''
      
      try {
        const response = await axios.get('https://api.modernkurs.com/api/statistics/admin')
        
        if (response.data.success) {
          statistics.value = response.data.data
        } else {
          error.value = response.data.error || 'İstatistikler alınamadı'
        }
      } catch (err) {
        console.error('İstatistik yükleme hatası:', err)
        error.value = 'İstatistikler yüklenirken bir hata oluştu'
      } finally {
        isLoading.value = false
      }
    }
    
    const getBarHeight = (count) => {
      if (!statistics.value?.weekly_statistics) return '0%'
      const maxCount = Math.max(...statistics.value.weekly_statistics.map(w => w.appointment_count))
      if (maxCount === 0) return '0%'
      return `${(count / maxCount) * 100}%`
    }
    
    const formatWeekDate = (weekStart) => {
      if (!weekStart) return ''
      const date = new Date(weekStart)
      const endDate = new Date(date)
      endDate.setDate(date.getDate() + 6)
      
      return `${date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' })} - ${endDate.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' })}`
    }
    
    // Lifecycle
    onMounted(async () => {
      // Token geçerliliğini kontrol et
      const isTokenValid = await store.dispatch('checkTokenValidity')
      if (!isTokenValid) {
        router.push('/login')
        return
      }
      
      // Rol kontrolü
      if (currentUser.value?.role !== 'admin') {
        router.push('/login')
        return
      }
      
      await fetchStatistics()
    })
    
    return {
      isLoading,
      error,
      statistics,
      weeklyStats,
      fetchStatistics,
      getBarHeight,
      formatWeekDate
    }
  }
}
</script>

<style scoped>
.admin-statistics {
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
}

.page-header {
  text-align: center;
  margin-bottom: 3rem;
}

.page-header h1 {
  font-size: 2.5rem;
  color: #333;
  margin-bottom: 0.5rem;
}

.page-header p {
  font-size: 1.1rem;
  color: #666;
}

/* Loading State */
.loading-state {
  text-align: center;
  padding: 4rem;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #4CAF50;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Error State */
.error-state {
  text-align: center;
  padding: 4rem;
  background: #fff5f5;
  border-radius: 12px;
  border: 1px solid #fed7d7;
}

.error-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.error-state h3 {
  color: #c53030;
  margin-bottom: 1rem;
}

.error-state p {
  color: #742a2a;
  margin-bottom: 1.5rem;
}

.btn-retry {
  background: #4CAF50;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
}

/* Overview Cards */
.overview-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.stat-icon {
  font-size: 3rem;
}

.stat-content {
  display: flex;
  flex-direction: column;
}

.stat-number {
  font-size: 2.5rem;
  font-weight: bold;
  color: #333;
}

.stat-label {
  font-size: 1rem;
  color: #666;
  margin-top: 0.25rem;
}

.total-students {
  border-left: 4px solid #2196F3;
}

.total-teachers {
  border-left: 4px solid #FF9800;
}

.total-appointments {
  border-left: 4px solid #4CAF50;
}

.current-week {
  border-left: 4px solid #9C27B0;
}

/* Chart Section */
.chart-section {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  margin-bottom: 3rem;
}

.chart-section h2 {
  margin-bottom: 2rem;
  color: #333;
  text-align: center;
}

.weekly-chart {
  display: flex;
  justify-content: space-around;
  align-items: end;
  gap: 1rem;
  min-height: 300px;
  padding: 2rem 0;
}

.week-bar {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  flex: 1;
}

.bar-container {
  width: 100%;
  height: 200px;
  display: flex;
  align-items: end;
  justify-content: center;
}

.bar {
  width: 80%;
  background: linear-gradient(135deg, #4CAF50, #45a049);
  border-radius: 8px 8px 0 0;
  min-height: 20px;
  transition: all 0.3s ease;
  cursor: pointer;
}

.bar:hover {
  transform: scaleY(1.05);
  box-shadow: 0 4px 15px rgba(76, 175, 80, 0.3);
}

.week-label {
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.week-number {
  font-weight: bold;
  color: #333;
  font-size: 0.9rem;
}

.week-date {
  font-size: 0.8rem;
  color: #666;
}

/* Top Performers Section */
.top-performers {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 3rem;
}

.top-students,
.top-teachers {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.top-students h2,
.top-teachers h2 {
  margin-bottom: 1.5rem;
  color: #333;
  text-align: center;
}

.performer-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.performer-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.performer-item:hover {
  background: #e9ecef;
  transform: translateX(5px);
}

.rank {
  width: 30px;
  height: 30px;
  background: #4CAF50;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 0.9rem;
}

.performer-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
}

.name {
  font-weight: bold;
  color: #333;
}

.count {
  font-size: 0.9rem;
  color: #666;
}

/* Table Section */
.table-section {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.table-section h2 {
  margin-bottom: 2rem;
  color: #333;
  text-align: center;
}

.stats-table {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
}

.table-header {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
}

.header-cell {
  padding: 1rem;
  font-weight: bold;
  color: #333;
  text-align: center;
}

.table-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  border-bottom: 1px solid #e0e0e0;
}

.table-row:last-child {
  border-bottom: none;
}

.table-cell {
  padding: 1rem;
  text-align: center;
  color: #333;
}

.appointment-count {
  background: #4CAF50;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-weight: bold;
}

/* No Data State */
.no-data-state {
  text-align: center;
  padding: 4rem;
  background: #f8f9fa;
  border-radius: 12px;
}

.no-data-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.no-data-state h3 {
  color: #333;
  margin-bottom: 1rem;
}

.no-data-state p {
  color: #666;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .top-performers {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .admin-statistics {
    padding: 1rem;
  }
  
  .page-header h1 {
    font-size: 2rem;
  }
  
  .overview-cards {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
  
  .stat-card {
    padding: 1.5rem;
  }
  
  .weekly-chart {
    flex-direction: column;
    align-items: center;
    gap: 2rem;
  }
  
  .week-bar {
    width: 100%;
  }
  
  .bar-container {
    height: 150px;
  }
  
  .stats-table {
    font-size: 0.9rem;
  }
  
  .table-header,
  .table-row {
    grid-template-columns: 1fr 1fr 1fr;
  }
  
  .header-cell,
  .table-cell {
    padding: 0.75rem 0.5rem;
  }
}

@media (max-width: 480px) {
  .page-header h1 {
    font-size: 1.8rem;
  }
  
  .overview-cards {
    grid-template-columns: 1fr;
  }
  
  .stat-card {
    padding: 1rem;
    gap: 1rem;
  }
  
  .stat-icon {
    font-size: 2rem;
  }
  
  .stat-number {
    font-size: 2rem;
  }
  
  .chart-section,
  .table-section,
  .top-students,
  .top-teachers {
    padding: 1rem;
  }
  
  .weekly-chart {
    gap: 1.5rem;
  }
  
  .bar-container {
    height: 120px;
  }
  
  .table-header,
  .table-row {
    grid-template-columns: 1fr 1fr 1fr;
    font-size: 0.8rem;
  }
  
  .header-cell,
  .table-cell {
    padding: 0.5rem 0.25rem;
  }
  
  .performer-item {
    padding: 0.75rem;
  }
  
  .rank {
    width: 25px;
    height: 25px;
    font-size: 0.8rem;
  }
}
</style>
